<template>
    <component
        v-for="data in objects"
        :key="data.id"
        :is="data.component"
        :object="data.object"
        :visible="categoryId === data.id"
        :layer="layer"
    />

    <!-- <BackgroundShape
        v-for="(data, index) in packTiers"
        :key="data.id"
        :layer="layer"
        :material="iridescentMaterial"
        :tier="data.id"
        :visible="index + 1 === tier"
    /> -->

    <BaseThreeObject v-bind="backgroundProps">
        <component
            v-for="data in bgTypes"
            :key="data.id"
            :is="data.component"
            :layer="layer"
            :visible="tier === data.id"
        />
    </BaseThreeObject>

    <Category :layer="layer" :text="categoryText" />
</template>

<script setup>
    import { DirectionalLight, RepeatWrapping } from 'three';
    import { computed, onMounted, reactive, shallowRef, watch } from 'vue';

    import { requestIdleCallback } from '@resn/gozer-misc';
    import {
        BaseThreeObject,
        usePane,
        useRafBool,
        useRenderer,
        useThreeObject,
    } from '@resn/gozer-vue';

    import SimpleIrridescentMaterial from '../../materials/SimpleIrridescentMaterial';
    import BackgroundShape from './backgrounds/BackgroundShape.vue';
    import InfinityBg from './backgrounds/InfinityBg.vue';
    import MegaBg from './backgrounds/MegaBg.vue';
    import SuperBg from './backgrounds/SuperBg.vue';
    import UltraBg from './backgrounds/UltraBg.vue';
    import Baseball from './Baseball.vue';
    import Basketball from './Basketball.vue';
    import Category from './Category.vue';
    import Football from './Football.vue';
    import MultiSport from './MultiSport.vue';
    import Pokeball from './Pokeball.vue';
    import { useLoaderComplete } from '~/composables/useLoaderComplete';
    import { LAYER_PACK_SCENE, PackTiersArray } from '~/core/constants';
    import { useGlobalAssets } from '~/providers/GlobalAssets';
    import { formatCategoryText } from '~/utils/text-utils';

    const props = defineProps({
        active: {
            type: Boolean,
            default: false,
        },
        color: { type: String, default: '#6044FF' },
        tier: { type: Number, default: 1 },
        category: { type: String, default: 'basketball' },
        layer: { type: Number, default: LAYER_PACK_SCENE },
    });

    const backgroundProps = reactive({
        s: 1.25,
        pz: -0.5,
    });

    const { object } = useThreeObject(null, {
        name: 'PackScene',
        dispose: true,
        props: {
            s: 1.5,
            py: -0.22,
            pz: -0.2,
        },
    });

    const componentTypes = [
        { id: 'basketball', name: 'basketball', component: Basketball },
        { id: 'baseball', name: 'baseball', component: Baseball },
        { id: 'football', name: 'football', component: Football },
        { id: 'multisport', name: 'Scene', component: MultiSport },
        { id: 'pokemon', name: 'pokeball', component: Pokeball },
    ];

    const bgTypes = [
        { id: 1, component: SuperBg },
        { id: 2, component: MegaBg },
        { id: 3, component: UltraBg },
        { id: 4, component: InfinityBg },
    ];

    const packTiers = PackTiersArray.filter((t, i) => i < 3);

    const objects = shallowRef([]);

    const iridescentMaterial = new SimpleIrridescentMaterial();

    const active = computed(() => props.active);
    const categoryId = computed(() =>
        typeof props.category === 'string' ? props.category.toLowerCase() : ''
    );

    const categoryText = computed(() => formatCategoryText(categoryId.value ?? ''));

    // const light = new DirectionalLight();
    // light.position.set(1, 1, 1);
    // object.add(light);

    onMounted(() => {
        setColor(props.color);
        setTier(props.tier);
        setCategory(props.category);
    });

    const setColor = (color) => {};

    const setTier = (tier) => {
        // console.log('🚀 ~ setTier ~ tier:', tier);
    };

    const setCategory = (category) => {
        // console.log('🚀 ~ setCategory ~ category:', category);
    };

    let x = 0;
    const setupScene = () => {
        // const { gltf } = localAssets.data;

        const { matcapPackGlobal, matcapIrridescent3, packSceneObjects } = globalAssets.assets;
        iridescentMaterial.tMatcap = matcapIrridescent3;

        const _objects = [];
        const scene = packSceneObjects.scene.clone(true);
        scene.traverse((child) => {
            const component = componentTypes.find((c) => c.name === child.name);

            if (component) {
                // child.position.set(x, 0, 0);
                // x += 0.5;

                _objects.push({
                    id: component.id,
                    object: child,
                    component: component.component,
                });

                // child.geometry.computeBoundingBox();
                // console.log('🚀 ~ setupScene ~ child:', child.geometry.boundingBox);
            }

            // child.material = objectMaterial;
            // child.material = customMaterial;
        });

        objects.value = _objects;

        object.add(scene);

        requestAnimationFrame(scenePostSetup);
    };

    const scenePostSetup = () => {
        requestIdleCallback(() => {
            // renderer.compile(object, camera);
        });
    };

    const globalAssets = useGlobalAssets(setupScene);

    // const localAssets = useLoaderComplete(
    //     {
    //         gltf: '/glb/pack/pack-scene-objects-etc1s@sm.glb',
    //     },
    //     () => {
    //         if (globalAssets.assets) setupScene();
    //     }
    // );

    watch(() => props.tier, setTier);
    watch(() => props.color, setColor);
    watch(() => props.category, setCategory);

    useRafBool(active, ({ delta }) => {
        const rotation = (delta / 1000) * 0.8;

        iridescentMaterial.u_matcapRotation += rotation;
        // objectMaterial.uniforms.u_matcapRotation.value += rotation;
    });

    // usePane(
    //     [
    //         {
    //             name: 'Bg Props',
    //             value: backgroundProps,
    //             options: {
    //                 min: -5,
    //                 max: 5,
    //                 step: 0.001,
    //             },
    //         },
    //     ],
    //     { title: 'Background Container', expanded: false }
    // );
</script>
